import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { MaskedLines, Eyebrow, EASE } from "../site/Motion";

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 80, damping: 18 });
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 80, damping: 18 });
  const sunX = useSpring(useTransform(mx, [-0.5, 0.5], [-30, 30]), { stiffness: 50, damping: 20 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} className="relative overflow-hidden bg-[#FDFBF7] pb-16 pt-32 sm:pt-40 lg:min-h-screen lg:pb-24" data-testid="home-hero">
      <div className="relative mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:px-12">
        <motion.div style={{ y: textY }} className="relative z-10 lg:col-span-7 lg:pt-10">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }}>
            <Eyebrow>Columbus, Ohio · Behavioral health</Eyebrow>
          </motion.div>
          <MaskedLines
            lines={[
              "Recovery,",
              <>held with <span className="italic text-[#A9533A]">care</span></>,
              "and a plan",
              <>that's <span className="italic">yours.</span></>,
            ]}
            className="font-serif-display mt-6 text-[3.4rem] leading-[0.92] tracking-tight text-[#1E2621] sm:text-8xl lg:text-[8.2rem]"
            testid="hero-heading"
          />
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 0.85 }}>
            <p className="mt-10 max-w-md text-base leading-relaxed text-[#4A554D] md:text-lg">
              Peer Support, CDCA Counseling and Life Coaching, in-person or by telehealth, built around your personal recovery goals.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/contact" className="group inline-flex items-center gap-3 rounded-full bg-[#1E2621] py-2 pl-7 pr-2 text-sm font-medium text-[#F4F1EA] transition-colors hover:bg-[#A9533A]" data-testid="hero-primary-cta">
                Start a conversation
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#C86D51] transition-transform duration-500 group-hover:rotate-45 group-hover:bg-[#1E2621]">
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </Link>
              <Link to="/services#paying" className="group inline-flex items-center gap-2 text-sm font-medium text-[#1E2621]" data-testid="hero-paying-link">
                How paying works
                <ArrowDownRight className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
              </Link>
            </div>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5" style={{ perspective: 1200 }}>
          <motion.div aria-hidden="true" style={{ x: sunX }} initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.6, ease: EASE, delay: 0.2 }}
            className="absolute -left-6 top-6 h-48 w-48 rounded-full bg-[#C86D51] sm:h-64 sm:w-64" />
          <motion.div
            style={{ rotateX: rotX, rotateY: rotY, transformStyle: "preserve-3d" }}
            initial={{ clipPath: "inset(100% 0% 0% 0% round 2rem)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0% round 2rem)" }}
            transition={{ duration: 1.5, ease: EASE, delay: 0.35 }}
            className="relative w-full overflow-hidden rounded-[2rem] rounded-tr-[5rem] shadow-[0_40px_80px_-30px_rgba(30,38,33,0.45)]"
            data-testid="hero-image-frame"
          >
            <img src="/images/hero.jpg" alt="A calm, supportive one-on-one counseling conversation" className="block h-auto w-full" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1B241E]/20 via-transparent to-transparent" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 1.2 }}
            className="relative mt-5 w-full rounded-2xl border border-[#E2DCD0] bg-[#FDFBF7]/90 p-5 shadow-xl backdrop-blur-md lg:absolute lg:bottom-6 lg:left-0 lg:mt-0 lg:max-w-[15rem]"
            data-testid="hero-telehealth-badge"
          >
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.18em] text-[#A9533A]">In-person or telehealth</p>
            <p className="font-serif-display mt-2 text-xl leading-snug text-[#1E2621]">Individual or group sessions, at your pace.</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
